import { getGuidelineDisplayName } from './accuracyGuidelineLabels';
import { draftContentToYaml } from './guidelineDraftYaml';
import type {
  GuidelineDraftCategory,
  GuidelineDraftContent,
  GuidelineDraftRule,
} from '../types/accuracy';
import type { GuidelinePromptField } from '../mock/guidelineSnippets';

const FIELD_LABELS: Record<GuidelinePromptField, string> = {
  existence_check_prompt: '存在チェック',
  violation_check_prompt: '違反チェック',
};

export type SideLineKind = 'equal' | 'remove' | 'add' | 'empty';

export interface SideBySideLine {
  text: string;
  kind: SideLineKind;
}

export interface SideBySideRow {
  left: SideBySideLine;
  right: SideBySideLine;
}

export interface GuidelinePromptDiffSection {
  id: string;
  pathLabel: string;
  fieldLabel: string;
  rows: SideBySideRow[];
  hasChanges: boolean;
}

function splitLines(text: string | undefined): string[] {
  if (!text) return [];
  return text.split(/\r?\n/).map((l) => l.trimEnd());
}

/** LCS-based line alignment for left (original) vs right (draft). */
export function computeSideBySideRows(before: string[], after: string[]): SideBySideRow[] {
  const n = before.length;
  const m = after.length;

  if (n === 0 && m === 0) return [];

  if (before.join('\n') === after.join('\n')) {
    return before.map((text) => ({
      left: { text, kind: 'equal' as const },
      right: { text, kind: 'equal' as const },
    }));
  }

  const dp: number[][] = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] =
        before[i] === after[j]
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const rows: SideBySideRow[] = [];
  let i = 0;
  let j = 0;

  while (i < n || j < m) {
    if (i < n && j < m && before[i] === after[j]) {
      rows.push({
        left: { text: before[i], kind: 'equal' },
        right: { text: after[j], kind: 'equal' },
      });
      i++;
      j++;
    } else if (j < m && (i >= n || dp[i][j + 1] >= dp[i + 1][j])) {
      rows.push({
        left: { text: '', kind: 'empty' },
        right: { text: after[j], kind: 'add' },
      });
      j++;
    } else if (i < n) {
      rows.push({
        left: { text: before[i], kind: 'remove' },
        right: { text: '', kind: 'empty' },
      });
      i++;
    }
  }

  return rows;
}

function categoryPath(
  content: GuidelineDraftContent,
  category: GuidelineDraftCategory,
  ruleLabel: string
): string {
  return `${getGuidelineDisplayName(content.guideline_id)} › v${content.base_version} › ${category.violation_category} › ${ruleLabel}`;
}

function pushFieldSection(
  sections: GuidelinePromptDiffSection[],
  content: GuidelineDraftContent,
  category: GuidelineDraftCategory,
  rule: GuidelineDraftRule,
  field: GuidelinePromptField,
  beforeText: string | undefined,
  afterText: string | undefined
) {
  const before = splitLines(beforeText);
  const after = splitLines(afterText);
  const rows = computeSideBySideRows(before, after);
  const hasChanges = rows.some(
    (r) => r.left.kind === 'remove' || r.right.kind === 'add'
  );

  if (!hasChanges && before.length === 0 && after.length === 0) return;

  const ruleLabel = rule.name || rule.id;
  sections.push({
    id: `${category.id}:${rule.id}:${field}`,
    pathLabel: categoryPath(content, category, ruleLabel),
    fieldLabel: FIELD_LABELS[field],
    rows,
    hasChanges,
  });
}

export function buildGuidelineSideBySideSections(
  original: GuidelineDraftContent,
  draft: GuidelineDraftContent | null
): GuidelinePromptDiffSection[] {
  if (!draft) return [];

  const sections: GuidelinePromptDiffSection[] = [];

  for (const afterCat of draft.violation_categories) {
    const beforeCat = original.violation_categories.find((c) => c.id === afterCat.id);
    for (const afterRule of afterCat.rules) {
      const beforeRule = beforeCat?.rules.find((r) => r.id === afterRule.id);
      for (const field of ['existence_check_prompt', 'violation_check_prompt'] as const) {
        pushFieldSection(
          sections,
          draft,
          afterCat,
          afterRule,
          field,
          beforeRule?.[field],
          afterRule[field]
        );
      }
    }
  }

  return sections;
}

/** Line diff of YAML serialization (same text as YAML editor tab). */
export function buildYamlSideBySideSection(
  original: GuidelineDraftContent,
  draft: GuidelineDraftContent | null
): GuidelinePromptDiffSection | null {
  if (!draft) return null;
  return buildYamlSideBySideSectionFromText(
    draftContentToYaml(original),
    draftContentToYaml(draft)
  );
}

export function buildYamlSideBySideSectionFromText(
  beforeText: string,
  afterText: string
): GuidelinePromptDiffSection | null {
  const before = splitLines(beforeText);
  const after = splitLines(afterText);
  const rows = computeSideBySideRows(before, after);
  const hasChanges = rows.some((r) => r.left.kind === 'remove' || r.right.kind === 'add');

  return {
    id: 'yaml',
    pathLabel: 'YAML',
    fieldLabel: '行単位比較',
    rows,
    hasChanges,
  };
}

export interface YamlDiffLineMapping {
  rowIndex: number;
  /** 1-based CodeMirror line number, null when aligned row has no line on this side */
  leftLine: number | null;
  rightLine: number | null;
  leftKind: SideLineKind;
  rightKind: SideLineKind;
}

/** Map LCS-aligned diff rows to 1-based line numbers in each YAML document. */
export function buildYamlDiffLineMappings(rows: SideBySideRow[]): YamlDiffLineMapping[] {
  let leftIdx = 0;
  let rightIdx = 0;

  return rows.map((row, rowIndex) => {
    let leftLine: number | null = null;
    let rightLine: number | null = null;

    if (row.left.kind === 'remove' || row.left.kind === 'equal') {
      leftIdx += 1;
      leftLine = leftIdx;
    }
    if (row.right.kind === 'add' || row.right.kind === 'equal') {
      rightIdx += 1;
      rightLine = rightIdx;
    }

    return {
      rowIndex,
      leftLine,
      rightLine,
      leftKind: row.left.kind,
      rightKind: row.right.kind,
    };
  });
}

export function buildYamlDiffLineMappingsFromText(
  beforeText: string,
  afterText: string
): YamlDiffLineMapping[] {
  const before = splitLines(beforeText);
  const after = splitLines(afterText);
  return buildYamlDiffLineMappings(computeSideBySideRows(before, after));
}

export function buildGuidelineSideBySideSectionsForMode(
  original: GuidelineDraftContent,
  draft: GuidelineDraftContent | null,
  mode: 'yaml' | 'fields'
): GuidelinePromptDiffSection[] {
  if (mode === 'yaml') {
    const section = buildYamlSideBySideSection(original, draft);
    return section ? [section] : [];
  }
  return buildGuidelineSideBySideSections(original, draft);
}

export function filterChangedSections(
  sections: GuidelinePromptDiffSection[]
): GuidelinePromptDiffSection[] {
  return sections.filter((s) => s.hasChanges);
}

export interface DiffChangeLocation {
  key: string;
  sectionId: string;
  rowIndex: number;
  kind: 'remove' | 'add' | 'change';
  summary: string;
  sectionLabel: string;
}

export function collectDiffChangeLocations(
  sections: GuidelinePromptDiffSection[]
): DiffChangeLocation[] {
  const locations: DiffChangeLocation[] = [];

  for (const section of sections) {
    section.rows.forEach((row, rowIndex) => {
      const removed = row.left.kind === 'remove';
      const added = row.right.kind === 'add';
      if (!removed && !added) return;

      const previewSource = added ? row.right.text : row.left.text;
      const preview = previewSource.trim().slice(0, 72);

      locations.push({
        key: `${section.id}:${rowIndex}`,
        sectionId: section.id,
        rowIndex,
        kind: removed && added ? 'change' : removed ? 'remove' : 'add',
        summary: preview || (removed ? '削除された行' : '追加された行'),
        sectionLabel: section.pathLabel,
      });
    });
  }

  return locations;
}
